import React, { forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';

import { DialogControls } from '../../../components/DialogControls/DialogControls';
import { useHideModal } from '../../../actions/modal';
import { onCheckFuelInfo, savePointData } from '../../../actions/navigation';
import { DateTimePicker } from '../../../components/DateTimePicker/DateTimePicker';
import { convertFieldsValidator, getDeclarationFieldName, getFieldLabel, getFields, getFieldType, PointFields } from '../tools';
import { useStyles } from './useStyles';
import { useMobileWidth } from '../../../components/hooks/useMobileWidth';
import { useForm } from '../../../components/hooks/useForm/useForm';
import { NavType } from '../../../components/NavigationRoutes/constants';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '../../../components/material-ui/Dialog';
import { FormControl } from '../../../components/material-ui/FormControl';
import { TextField } from '../../../components/material-ui/TextField';
import { Typography } from '../../../components/material-ui/Typography';
import { useColumnsDeclaration } from '../../../selectors/routesDeclaration';
import { useSingleConvert } from '../../../components/hooks/useMeasureConverter';
import { useAlert } from '../hooks/useAlert';
import { AlertType } from '../../../reducers/alert';

const fieldList = [PointFields.FL, PointFields.ACT, PointFields.ETO, PointFields.ATO, PointFields.COMMENT];
const convertedFields = [PointFields.ACT];

export const ShortPoint = forwardRef(({ pos, point, title, toggleUpdate, iUserInit }, ref) => {
    const legId = useSelector((state) => state.screen.currentLeg);
    const startAlert = useAlert(AlertType.FUEL, toggleUpdate);
    const classes = useStyles();
    const { isMobilePortrait } = useMobileWidth();
    const columnsDeclaration = useColumnsDeclaration();
    const hideModal = useHideModal();
    const dispatch = useDispatch();
    const { t: translate } = useTranslation();
    const handleSuccess = useCallback(
        (data) => {
            dispatch(savePointData(pos, data));
            dispatch(onCheckFuelInfo());
            startAlert();
            hideModal();
        },
        [startAlert, legId, pos]
    );

    const handleCancel = useCallback(() => {
        if (!iUserInit) {
            startAlert();
        }

        hideModal();
    }, [iUserInit, startAlert]);

    const fields = useMemo(() => getFields(fieldList), []);
    const convert = useSingleConvert();
    useMemo(() => convertFieldsValidator(convertedFields, fields, convert), []);

    const { inputs, handleInputChange, handleSubmit, status } = useForm({
        onSuccess: handleSuccess,
        initialData: {
            ...point,
            type: point.type || NavType.FLIGHT,
            dateCreate: moment.utc(point.dateCreate),
            eto: point.eto ? moment.utc(point.eto) : null,
            ato: point.ato ? moment.utc(point.ato) : moment.utc(),
        },
        fields,
    });

    const handleChangeField = useCallback((name, value) => {
        handleInputChange({ target: { name, value } });
    }, []);

    const disableSubmit = Boolean(
        fieldList.find((key) => Object.values(status || {}).some((item) => item) || (fields[key].required && !inputs[key]))
    );
    const actions = useMemo(
        () => [
            { label: translate('cancel').toUpperCase(), onClick: handleCancel },
            { label: translate('ok'), color: 'primary', type: 'submit', disabled: disableSubmit },
        ],
        [disableSubmit]
    );

    return (
        <Dialog ref={ref} open={true} fullScreen={isMobilePortrait} classes={{ paper: classes.paper }}>
            <DialogTitle className={classes.title}>{title}</DialogTitle>
            <form className={classes.fields} onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name={PointFields.FL}
                            label={getDeclarationFieldName(PointFields.FL, columnsDeclaration)}
                            placeholder={getDeclarationFieldName(PointFields.FL, columnsDeclaration)}
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.FL)}
                            helperText={status[PointFields.FL] || get(inputs, PointFields.FL_DEFAULT, '')}
                            FormHelperTextProps={{ classes: { root: classes.fl } }}
                            error={Boolean(status[PointFields.FL])}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, PointFields.FL, '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name={PointFields.ACT}
                            label={getDeclarationFieldName(PointFields.ACT, columnsDeclaration)}
                            placeholder={getDeclarationFieldName(PointFields.ACT, columnsDeclaration)}
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.ACT)}
                            helperText={status[PointFields.ACT]}
                            error={Boolean(status[PointFields.ACT])}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, PointFields.ACT, '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <DateTimePicker
                        className={classes.picker}
                        name={PointFields.ATO}
                        canBeEmpty={true}
                        value={get(inputs, PointFields.ATO)}
                        labelTextTime={getDeclarationFieldName(PointFields.ATO, columnsDeclaration)}
                        onChange={handleChangeField}
                    />
                    <FormControl className={classes.comment}>
                        <TextField
                            name={PointFields.COMMENT}
                            label={getFieldLabel(PointFields.COMMENT)}
                            onChange={handleInputChange}
                            helperText={status[PointFields.COMMENT]}
                            error={Boolean(status[PointFields.COMMENT])}
                            fullWidth={true}
                            multiline={true}
                            value={get(inputs, PointFields.COMMENT, '')}
                            minRows={1}
                            maxRows={5}
                            className={classes.textarea}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <Typography color="error">{status.message}</Typography>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </form>
        </Dialog>
    );
});
